import { FormikProvider, useFormik } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { useFeatureDetails } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Subheading } from '@treams/treams-ui';

import LearningEventsList from '../components/LearningEventsList';
import LearningEventsSlider from '../components/LearningEventsSlider';
import LearningExploreViewToggle from '../components/LearningExploreViewToggle';
import LearningFilterSidebar from '../components/LearningFilterSidebar';
import LearningRecommendations from '../components/LearningRecommendations';
import useLearningRecommendations from '../hooks/useLearningRecommendations';

export default function LearningEventsContainer() {
	const { t } = useTranslation();

	const formik = useFormik({
		initialValues: {
			types: [],
			subjects: [],
		},
		onSubmit: () => {},
	});

	const [upcomingEventsKey, setUpcomingEventsKey] = useState(Math.random());
	const goodHabitzFeature = useFeatureDetails(graphql.FeatureName.Goodhabitz);
	const coursesAvailable = !goodHabitzFeature.loading && goodHabitzFeature.available;

	const { data, loading } = graphql.useLearningEventsQuery({
		variables: {
			first: 100,
			filters: {
				isArchived: false,
				learningTypeIDs: formik.values.types,
				learningSubjectIDs: formik.values.subjects,
			},
		},
	});

	const { data: coursesData, loading: coursesLoading } = graphql.useLearningCoursesQuery({
		skip: !coursesAvailable,
		variables: {
			first: 1000,
			filters: {
				learningTypeIDs: formik.values.types,
				learningSubjectIDs: formik.values.subjects,
			},
		},
	});

	const learningEvents = {
		edges: data?.learningEvents.edges,
	} as graphql.LearningEventConnection;

	learningEvents.edges = learningEvents?.edges?.filter(
		({ node }) => node.isViewerAttending || node.viewerCanJoin,
	);

	const recommendations = useLearningRecommendations();
	const recommendedCourseIDs = new Set(
		recommendations.recommendations.map((recommendation) => recommendation.learningCourse.id),
	);
	const filteredCourses = (coursesData?.learningCourses.edges ?? []).map(({ node }) => node);
	const courses = filteredCourses.filter((course) => !recommendedCourseIDs.has(course.id));
	const hasFilteredRecommendations = courses.length < filteredCourses.length;

	return (
		<>
			<Box mb={[4, 4, 5]}>
				<LearningEventsSlider key={upcomingEventsKey} title={t('learning.events.upcoming.title')} />
			</Box>

			<LearningRecommendations
				available={recommendations.available}
				loading={recommendations.loading}
				recommendations={recommendations.recommendations}
			/>

			<Flex mb={3} justifyContent="space-between" alignItems="center">
				<Subheading mb={0}>{t('learning.events.explore.title')}</Subheading>
				<LearningExploreViewToggle />
			</Flex>

			<Flex mt={4} flexWrap="wrap">
				<Box width={[1, 1, 1 / 3, 1 / 4]} pr={[0, 0, 3, 4]}>
					<FormikProvider value={formik}>
						<LearningFilterSidebar />
					</FormikProvider>
				</Box>

				<Box width={[1, 1, 2 / 3, 3 / 4]}>
					<LearningEventsList
						loading={loading || coursesLoading}
						events={learningEvents as graphql.LearningEventConnection}
						courses={courses}
						hasRecommendations={hasFilteredRecommendations}
					/>
				</Box>
			</Flex>
		</>
	);
}
